import api from '../../../../../api';

type ExerciseScore = {
  achievable: number;
  achieved: number;
  maximum: number;
};

export type InitFreeExerciseSessionResponse = {
  result?: {
    amountOfItems: number;
    duration: number;
    score: ExerciseScore;
    session: string;
    needsGrading: boolean;
  };
  resumable: boolean;
};

export type InitAssignmentSessionResponse = InitFreeExerciseSessionResponse & {
  restartable: boolean;
  isExpired: boolean;
};

export type InitExerciseSessionResponse = InitFreeExerciseSessionResponse | InitAssignmentSessionResponse;

type ExerciseUrlResponse = {
  exerciseUrl: string;
};

export type PauseExerciseSessionResponse = {
  result: {
    amountOfItems: number;
    duration: number;
    score: ExerciseScore;
  };
  notRestartable?: boolean;
};

export type StopExerciseSessionResponse = {
  result: {
    amountOfItems: number;
    duration: number;
    score: ExerciseScore;
    session?: string;
  };
  notRestartable?: boolean;
};

type ReviewExerciseSessionResponse = {
  reviewUrl: string;
};

export async function initFreeExerciseSession(
  token: string,
  moduleId: string,
  medialinkId: string,
  studioGroup: string,
): Promise<InitFreeExerciseSessionResponse> {
  const { data } = await api.get<InitFreeExerciseSessionResponse>(
    `/studio/modules/${moduleId}/medialinks/${medialinkId}/exercise-session`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        studioGroup,
      },
    },
  );

  return data;
}

export async function initAssignmentSession(
  token: string,
  moduleId: string,
  assignmentId: string,
  studioGroup: string,
): Promise<InitAssignmentSessionResponse> {
  const { data } = await api.get<InitAssignmentSessionResponse>(
    `/studio/user/modules/${moduleId}/assignments/${assignmentId}/exercise-session`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        studioGroup,
      },
    },
  );

  return data;
}

export async function initFreeExerciseSessionScorableMaterial(
  token: string,
  moduleId: string,
  materialId: string,
  studioGroup: string,
): Promise<InitFreeExerciseSessionResponse> {
  const { data } = await api.get<InitFreeExerciseSessionResponse>(
    `/studio/user/modules/${moduleId}/user-material/${materialId}/exercise-session`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        studioGroup,
      },
    },
  );

  return data;
}

export async function startFreeExerciseSession(
  token: string,
  moduleId: string,
  medialinkId: string,
  language: string,
  studioGroup: string,
): Promise<string> {
  const {
    data: { exerciseUrl },
  } = await api.put<ExerciseUrlResponse>(
    `/studio/modules/${moduleId}/medialinks/${medialinkId}/exercise-session/start`,
    {
      language,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        studioGroup,
      },
    },
  );

  return exerciseUrl;
}

export async function pauseFreeExerciseSession(
  token: string,
  moduleId: string,
  medialinkId: string,
  studioGroup: string,
): Promise<PauseExerciseSessionResponse> {
  const { data } = await api.put<PauseExerciseSessionResponse>(
    `/studio/modules/${moduleId}/medialinks/${medialinkId}/exercise-session/pause`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        studioGroup,
      },
    },
  );

  return data;
}

export async function resumeFreeExerciseSession(
  token: string,
  moduleId: string,
  medialinkId: string,
  studioGroup: string,
): Promise<string> {
  const {
    data: { exerciseUrl },
  } = await api.put<ExerciseUrlResponse>(
    `/studio/modules/${moduleId}/medialinks/${medialinkId}/exercise-session/resume`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        studioGroup,
      },
    },
  );

  return exerciseUrl;
}

export async function stopFreeExerciseSession(
  token: string,
  moduleId: string,
  medialinkId: string,
  studioGroup: string,
): Promise<StopExerciseSessionResponse> {
  const { data } = await api.delete(`/studio/modules/${moduleId}/medialinks/${medialinkId}/exercise-session`, {
    headers: {
      Authorization: `Bearer ${token}`,
      studioGroup,
    },
  });

  return data;
}

export async function reviewExerciseSession(
  token: string,
  moduleId: string,
  medialinkId: string,
  sessionId: string,
  studioGroup: string,
): Promise<ReviewExerciseSessionResponse> {
  const { data } = await api.get<ReviewExerciseSessionResponse>(
    `/studio/modules/${moduleId}/medialinks/${medialinkId}/exercise-session/${sessionId}/review`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        studioGroup,
      },
    },
  );
  return data;
}

export async function startAssignmentSession(
  token: string,
  moduleId: string,
  assignmentId: string,
  language: string,
  studioGroup: string,
): Promise<string> {
  const {
    data: { exerciseUrl },
  } = await api.put<ExerciseUrlResponse>(
    `/studio/user/modules/${moduleId}/assignments/${assignmentId}/exercise-session/start`,
    {
      language,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        studioGroup,
      },
    },
  );

  return exerciseUrl;
}

export async function pauseAssignmentSession(
  token: string,
  moduleId: string,
  assignmentId: string,
  studioGroup: string,
): Promise<PauseExerciseSessionResponse> {
  const { data } = await api.put<PauseExerciseSessionResponse>(
    `/studio/user/modules/${moduleId}/assignments/${assignmentId}/exercise-session/pause`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        studioGroup,
      },
    },
  );

  return data;
}

export async function resumeAssignmentSession(
  token: string,
  moduleId: string,
  assignmentId: string,
  studioGroup: string,
): Promise<string> {
  const {
    data: { exerciseUrl },
  } = await api.put<ExerciseUrlResponse>(
    `/studio/user/modules/${moduleId}/assignments/${assignmentId}/exercise-session/resume`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        studioGroup,
      },
    },
  );

  return exerciseUrl;
}

export async function stopAssignmentSession(
  token: string,
  moduleId: string,
  assignmentId: string,
  studioGroup: string,
): Promise<StopExerciseSessionResponse> {
  const { data } = await api.delete(`/studio/user/modules/${moduleId}/assignments/${assignmentId}/exercise-session`, {
    headers: {
      Authorization: `Bearer ${token}`,
      studioGroup,
    },
  });

  return data;
}
